import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { Save, Plus, Trash2, Megaphone } from "lucide-react";

export default function AdminContent() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/admin/content").then(({ data }) => {
      const c = data.content || {};
      setContent({ notice: c.notice || "", notice_enabled: !!c.notice_enabled, articles: c.articles || [], faq: c.faq || [] });
    });
  }, []);

  const save = async () => {
    try { await api.put("/admin/content", { notice: content.notice, notice_enabled: content.notice_enabled, articles: content.articles, faq: content.faq }); toast.success("Content saved!"); }
    catch { toast.error("Save failed"); }
  };

  if (!content) return <Loader />;

  const upd = (idx, field, val) => setContent((c) => { const a = [...c.articles]; a[idx] = { ...a[idx], [field]: val }; return { ...c, articles: a }; });
  const addArticle = () => setContent((c) => ({ ...c, articles: [...c.articles, { id: `art-${Date.now()}`, title: "New Article", excerpt: "", body: "" }] }));
  const delArticle = (idx) => setContent((c) => ({ ...c, articles: c.articles.filter((_, i) => i !== idx) }));
  const updFaq = (idx, field, val) => setContent((c) => { const f = [...c.faq]; f[idx] = { ...f[idx], [field]: val }; return { ...c, faq: f }; });
  const addFaq = () => setContent((c) => ({ ...c, faq: [...c.faq, { q: "", a: "" }] }));
  const delFaq = (idx) => setContent((c) => ({ ...c, faq: c.faq.filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-5 max-w-3xl">
      <h1 className="text-2xl md:text-3xl font-heading font-extrabold">Content Management</h1>

      <div className="card-thali">
        <div className="flex items-center justify-between mb-2">
          <label className="font-heading font-bold flex items-center gap-2"><Megaphone className="w-5 h-5" /> Site Notice Banner</label>
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" data-testid="content-notice-toggle-label">
            <input type="checkbox" className="w-4 h-4 accent-thali-green" checked={!!content.notice_enabled} onChange={(e) => setContent({ ...content, notice_enabled: e.target.checked })} data-testid="content-notice-toggle" />
            {content.notice_enabled ? "ON" : "OFF"}
          </label>
        </div>
        <input className="input-thali" placeholder="Notice text (banner shows only when ON and not empty)" value={content.notice} onChange={(e) => setContent({ ...content, notice: e.target.value })} data-testid="content-notice" />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg">Articles</h2>
          <button onClick={addArticle} className="btn-ghost text-sm" data-testid="content-add-article"><Plus className="w-4 h-4" /> Add</button>
        </div>
        {content.articles.map((a, i) => (
          <div key={a.id || i} className="card-thali space-y-2" data-testid={`content-article-${i}`}>
            <div className="flex gap-2">
              <input className="input-thali flex-1" value={a.title} onChange={(e) => upd(i, "title", e.target.value)} placeholder="Title" />
              <button onClick={() => delArticle(i)} className="w-11 h-11 rounded-lg bg-red-50 text-red-600 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
            </div>
            <input className="input-thali" value={a.excerpt} onChange={(e) => upd(i, "excerpt", e.target.value)} placeholder="Short excerpt" />
            <textarea className="input-thali min-h-[80px]" value={a.body} onChange={(e) => upd(i, "body", e.target.value)} placeholder="Body" />
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg">FAQ</h2>
          <button onClick={addFaq} className="btn-ghost text-sm" data-testid="content-add-faq"><Plus className="w-4 h-4" /> Add</button>
        </div>
        {content.faq.map((f, i) => (
          <div key={i} className="card-thali space-y-2">
            <div className="flex gap-2">
              <input className="input-thali flex-1" value={f.q} onChange={(e) => updFaq(i, "q", e.target.value)} placeholder="Question" />
              <button onClick={() => delFaq(i)} className="w-11 h-11 rounded-lg bg-red-50 text-red-600 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
            </div>
            <textarea className="input-thali min-h-[60px]" value={f.a} onChange={(e) => updFaq(i, "a", e.target.value)} placeholder="Answer" />
          </div>
        ))}
      </div>

      <button onClick={save} className="btn-3d-green w-full" data-testid="content-save"><Save className="w-4 h-4" /> Save All Content</button>
    </div>
  );
}
